/**
 * Image processing primitives for the web photo upload pipeline (F16).
 *
 * Mirrors what `mobile/src/sync/syncPhotos.ts` does via
 * `expo-image-manipulator`: resize a captured photo to a small
 * thumbnail (180px) + a "full" 800px JPEG, both at JPEG quality 0.85
 * (full) / 0.6 (thumbnail). The web side gets the same numbers so a
 * photo uploaded from a laptop renders identically on every peer.
 *
 * We also re-encode through `<canvas>` rather than copying the raw
 * file bytes — that strips EXIF (location, original timestamp,
 * camera make/model) which is a deliberate privacy posture for the
 * cuadrilla web client. Mobile's resize pipeline strips EXIF too,
 * so this isn't web-only behaviour.
 *
 * Magic-byte validation lives here as a UX check, NOT a security
 * boundary: the relay is blind to encrypted blob contents and the
 * file is opaque past the AES-CBC layer. The check exists so the
 * dropzone can give the user a meaningful "Formato no soportado"
 * error before we sink time into resizing a non-image.
 */

/** Hard cap before resize: a single photo larger than this is
 *  rejected with a user-facing error. 5 MB is generous for phone-
 *  shot JPEGs (typically 1–3 MB) and stops accidental drag-and-drop
 *  of multi-MB raw files / scans from blowing up the canvas decode. */
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

export interface ResizedImage {
  /** `data:image/jpeg;base64,...` — ready to drop into `<img src>` or
   *  to feed `dataUrlToBase64` for the encrypt path. */
  dataUrl: string;
  /** Resulting bitmap width after the resize. */
  width: number;
  /** Resulting bitmap height after the resize. */
  height: number;
}

/**
 * Resize an image File to fit within `maxWidth` (preserving aspect
 * ratio) and re-encode as JPEG at the given quality. Always returns
 * a JPEG regardless of the input format (PNG, HEIC on Safari, etc.)
 * so the downstream encrypt + upload path can stay format-agnostic.
 *
 * Uses a regular `<canvas>` (not OffscreenCanvas) for the widest
 * browser support — Safari < 16.4 lacks OffscreenCanvas.
 *
 * Throws when the file fails to decode (unsupported format on this
 * browser, e.g. HEIC on Chrome) so the caller can surface a clear
 * error to the user.
 */
export async function resizeImage(
  file: File,
  maxWidth: number,
  quality: number = 0.85,
): Promise<ResizedImage> {
  if (maxWidth <= 0) throw new RangeError(`resizeImage: maxWidth must be positive (got ${maxWidth})`);
  if (quality <= 0 || quality > 1)
    throw new RangeError(`resizeImage: quality must be in (0, 1] (got ${quality})`);

  const img = await loadImageElement(file);
  try {
    const ratio = Math.min(1, maxWidth / img.naturalWidth);
    const targetW = Math.max(1, Math.round(img.naturalWidth * ratio));
    const targetH = Math.max(1, Math.round(img.naturalHeight * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2D context available — canvas is unsupported in this browser.');
    ctx.drawImage(img, 0, 0, targetW, targetH);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    return { dataUrl, width: targetW, height: targetH };
  } finally {
    if (img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
  }
}

/**
 * Load a File into an `<img>` element via a transient blob URL.
 * Resolves once `naturalWidth` is populated; rejects when the
 * browser refuses to decode the bytes.
 *
 * Caller is responsible for revoking `img.src` once the bitmap is
 * no longer needed (see `resizeImage`).
 */
function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        reject(new Error('Imagen vacía o ilegible.'));
        return;
      }
      resolve(img);
    };
    img.onerror = () =>
      reject(new Error('No se pudo decodificar la imagen. Convierte a JPEG o PNG e inténtalo de nuevo.'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Inspect the first 12 bytes of a file and confirm one of the
 * supported image magic-byte sequences. Throws a user-facing error
 * (in Spanish) when the format isn't recognised so the dropzone can
 * surface it as inline feedback.
 *
 * Supported:
 *   - JPEG (FFD8FF...)
 *   - PNG  (89 50 4E 47)
 *   - HEIC/HEIF (`...ftyp` at byte 4 — Safari decodes natively;
 *     Chrome may still reject in `loadImageElement`).
 *   - WebP (`RIFF....WEBP`).
 *
 * Note: this is UX defence only. The real upload format is whatever
 * the canvas re-encodes to (always JPEG), so a hostile peer can't
 * smuggle a non-image past this gate in any meaningful way.
 */
export async function validateMagicBytes(file: File): Promise<void> {
  if (file.size === 0) throw new Error('El archivo está vacío.');
  const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());

  const isJpeg = head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff;
  const isPng =
    head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47;
  // ISO base media file format; HEIC/HEIF wraps `ftyp` at offset 4.
  const isFtyp =
    head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70;
  // RIFF...WEBP: 'R','I','F','F' at 0, 'W','E','B','P' at 8.
  const isWebp =
    head[0] === 0x52 &&
    head[1] === 0x49 &&
    head[2] === 0x46 &&
    head[3] === 0x46 &&
    head[8] === 0x57 &&
    head[9] === 0x45 &&
    head[10] === 0x42 &&
    head[11] === 0x50;

  if (!isJpeg && !isPng && !isFtyp && !isWebp) {
    throw new Error('Formato no soportado. Usa JPEG, PNG, WebP o HEIC.');
  }
}

/**
 * Reject the file when its raw size exceeds `maxBytes`. The message
 * is user-facing: the dropzone prints it inline as the upload-blocked
 * reason so the user knows what to do next.
 */
export function validateSize(file: File, maxBytes: number = MAX_PHOTO_BYTES): void {
  if (file.size > maxBytes) {
    const mb = (maxBytes / (1024 * 1024)).toFixed(0);
    throw new Error(`La foto es demasiado grande. Máximo ${mb} MB.`);
  }
}

/**
 * Strip the `data:image/jpeg;base64,` prefix from a data URL and
 * return the inner base64 string (no padding-fix; canvas always
 * emits canonical base64).
 *
 * The encrypt path matches mobile's `encryptFile`:
 *   mobile reads the file as a base64 string, then encrypts that
 *   string. The web mirrors the shape exactly so a peer running
 *   either platform can decrypt + decode without branching on
 *   which side produced the blob.
 */
export function dataUrlToBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) throw new Error('Data URL inválida.');
  return dataUrl.slice(comma + 1);
}

/**
 * Decode a `data:image/...;base64,...` URL into raw image bytes.
 * Used in tests + by the upload pipeline when it needs the binary
 * (rather than the base64) — e.g. for a `Blob` we expose to the UI
 * as `<img src={localPath} />` via `URL.createObjectURL`.
 */
export function dataUrlToBytes(dataUrl: string): Uint8Array {
  const b64 = dataUrlToBase64(dataUrl);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
